export default class ObjectService {
  static prune(object, retainKeys = []) {
    if (typeof object !== 'object' || object === null || retainKeys.length === 0) {
      return object
    }

    if (Array.isArray(object)) {
      return object.map((item) => ObjectService.prune(item, retainKeys))
    }

    const prunedObject = {}

    retainKeys.forEach((key) => {
      if (typeof key === 'string' && key in object) {
        prunedObject[key] = object[key]
      }

      if (typeof key === 'object') {
        const nestedObjectName = Object.keys(key)[0]
        const nestedRetainKeys = key[nestedObjectName]

        if (nestedObjectName in object && Array.isArray(nestedRetainKeys)) {
          const nestedObject = object[nestedObjectName]
          const nestedObjectValue = ObjectService.prune(nestedObject, nestedRetainKeys)
          prunedObject[nestedObjectName] = nestedObjectValue
        }
      }
    })

    return prunedObject
  }

  static serialize(object, retainKeys = []) {
    if (typeof object !== 'object' || object === null) {
      return object
    }

    if (Array.isArray(object)) {
      return JSON.stringify(
        object.map((item) => ObjectService.serialize(item, retainKeys)),
      )
    }

    const prunedObject = ObjectService.prune(object, retainKeys)
    return JSON.stringify(prunedObject)
  }

  static size(object, unit = 'bytes') {
    const sizeInBytes = new TextEncoder().encode(JSON.stringify(object)).length
    if (unit === 'kb') {
      return (sizeInBytes / 1024).toFixed(2)
    }
    if (unit === 'mb') {
      return (sizeInBytes / (1024 * 1024)).toFixed(2)
    }
    return sizeInBytes.toFixed(2)
  }

  static findInArrayByKey(array, key) {
    return array.filter((item) => key in item)
  }

  static findInArrayByValue(array, value) {
    return array.filter((item) => Object.values(item).includes(value))
  }

  static findInArrayByKeyValue(array, key, value) {
    return array.filter((item) => item[key] === value)
  }

  static diffKeys(object1, object2, limitKeys) {
    const diff = []

    object1 = ObjectService.prune(object1, limitKeys)
    object2 = ObjectService.prune(object2, limitKeys)
    limitKeys.forEach((key) => {
      if (typeof key === 'object') {
        key = Object.keys(key)[0]
      }

      let value1
      let value2

      if (key in object1) {
        value1 = typeof object1[key] === 'object' ? JSON.stringify(object1[key]) : object1[key]
      }

      if (key in object2) {
        value2 = typeof object2[key] === 'object' ? JSON.stringify(object2[key]) : object2[key]
      }

      if (value1 !== value2) {
        diff.push(key)
      }
    })

    return diff
  }

  static compareArraysByKeys(array1, array2, limitKeys) {
    if (!Array.isArray(array1) || !Array.isArray(array2)) {
      throw new Error('Both arguments must be arrays')
    }
    const minLength = Math.min(array1.length, array2.length)
    const differences = []

    for (let i = 0; i < minLength; i++) {
      // Sử dụng hàm diffKeys đã có để so sánh từng object theo limitKeys
      const diffKeys = ObjectService.diffKeys(array1[i], array2[i], limitKeys)
      if (diffKeys.length > 0) {
        differences.push({
          index: i,
          diffKeys,
          object1: array1[i],
          object2: array2[i],
        })
      }
    }

    return differences
  }

  static diffKeyValues(object1, object2, limitKeys) {
    const diffKeysResult = ObjectService.diffKeys(object1, object2, limitKeys)
    const diffObject1 = {}
    const diffObject2 = {}

    diffKeysResult.forEach((key) => {
      if (key in object1) {
        diffObject1[key] = object1[key]
      }

      if (key in object2) {
        diffObject2[key] = object2[key]
      }
    })

    return [diffObject1, diffObject2]
  }

  static detectArrayChanges(oldArray, newArray, retainKeys) {
    const filterValid = (arr) => arr.filter((item) => item.id != null)

    const oldValid = filterValid(oldArray)
    const newValid = filterValid(newArray)

    const oldMap = new Map(oldValid.map((item) => [item.id, item]))
    const newMap = new Map(newValid.map((item) => [item.id, item]))

    // Process deletions and updates using map & filter
    const changesFromOld = Array.from(oldMap).map(([id, oldItem]) => {
      if (!newMap.has(id)) {
        return { action: 'delete', id }
      }
      const newItem = newMap.get(id)
      const diffs = {}
      retainKeys.forEach((key) => {
        if (oldItem[key] !== newItem[key]) {
          diffs[key] = newItem[key]
        }
      })
      // If there are differences (ignoring sole 'order' changes), record an update
      if (
        Object.keys(diffs).length > 0 &&

          !(Object.keys(diffs).length === 1 && Object.prototype.hasOwnProperty.call(diffs, 'order'))
      ) {
        return { action: 'update', id, diffs }
      }
      return null
    }).filter((change) => change !== null)

    const changesFromNew = Array.from(newMap).map(([id, newItem]) => {
      if (!oldMap.has(id)) {
        return { action: 'add', item: newItem }
      }
      return null
    }).filter((change) => change !== null)

    // Process order swaps (using the order property)
    const swaps = newValid.map((newItem) => {
      const oldItem = oldMap.get(newItem.id)
      if (oldItem && oldItem.order !== newItem.order) {
        return {
          action: 'swap',
          id: newItem.id,
          order: { old: oldItem.order, new: newItem.order },
        }
      }
      return null
    }).filter((change) => change !== null)

    return [...changesFromOld, ...changesFromNew, ...swaps]
  }
}
